/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memoize, MemoizedFunction } from 'lodash';
import React from 'react';

export type MapCache = Map<any, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface LoaderConfig<P = {}> {
    /* tslint:disable no-any */
    /**
     * @property memoizer
     * A function that returns a cache key for memoizing fetchData. https://lodash.com/docs/4.17.10#memoize
     * To opt out of memoization, have this return `false`.
     */
    memoizer: (props: P) => any;

    /**
     * @property fetchData
     * Called whenever needsData returns `true`. Put your redux actions that fetch requisite data in here.
     */
    fetchData: (props: P, showError: (error: string) => void, cache?: MapCache) => Promise<any>;

    /**
     * @property needsData
     * Called on componentDidMount and componentWillReceiveProps. Make this return `true` if data is needed.
     */
    needsData: (props: P, prevProps: P | null) => boolean;
    /* tslint:enable no-any */
}

export interface WithDataConfig {
    fallback?: () => JSX.Element;
    handleError: (e: Error, showError: (error: string) => void) => void;
    renderError: (error: string) => JSX.Element;
}

interface ConfigWrapper<P> {
    config: LoaderConfig<P>;
    memoizedValue: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    memoizedFetch: LoaderConfig<P>['fetchData'] & MemoizedFunction;
    isFetching: boolean;
}

const initialMemoizerValue = {};

const configureWithData = (withDataConfig: WithDataConfig) => {
    return function withData<P>(...configs: LoaderConfig<P>[]) {
        // allows multiple instances to share the same memoized fetchers
        const memoizedFetchFunctions = configs.map((config) => memoize(config.fetchData, config.memoizer));

        return (Component: React.ComponentClass<P>) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            return class WithData extends Component {
                // For debugging
                // eslint-disable-next-line react/static-property-placement
                static displayName = `WithData(${Component.displayName || Component.name})`;

                wrappers: ConfigWrapper<P>[];

                error = '';

                constructor(props: P) {
                    super(props);
                    this.wrappers = configs.map((config, i) => ({
                        config,
                        memoizedValue: initialMemoizerValue,
                        memoizedFetch: memoizedFetchFunctions[i],
                        isFetching: config.needsData(props, null),
                    }));
                }

                componentDidMount() {
                    this.processConfigs(this.props, null);
                    if (super.componentDidMount) {
                        super.componentDidMount();
                    }
                }

                componentDidUpdate(prevProps: P, prevState: any, snapshot: any) {
                    // eslint-disable-line @typescript-eslint/no-explicit-any
                    this.processConfigs(this.props, prevProps);
                    if (super.componentDidUpdate) {
                        super.componentDidUpdate(prevProps, prevState, snapshot);
                    }
                }

                showError = (error: string) => {
                    // setState({ error }) is avoided since messing with the wrapped component's state
                    // opens up a can of worms.
                    this.error = error;
                    this.forceUpdate();
                };

                async processConfigs(props: P, prevProps: P | null) {
                    const wrappersToFetch = this.wrappers.filter((wrapper) => {
                        const newMemoizedValue = wrapper.config.memoizer(props);
                        if (newMemoizedValue === wrapper.memoizedValue && newMemoizedValue !== false) {
                            return false;
                        }
                        const shouldFetch = wrapper.config.needsData(props, prevProps);
                        wrapper.memoizedValue = newMemoizedValue;
                        wrapper.isFetching = shouldFetch;
                        return shouldFetch;
                    });
                    if (!wrappersToFetch.length) {
                        return;
                    }
                    const promises = wrappersToFetch.map((wrapper) => {
                        return wrapper.memoizedValue === false
                            ? wrapper.config.fetchData(props, this.showError)
                            : wrapper.memoizedFetch(props, this.showError, wrapper.memoizedFetch.cache as MapCache);
                    });
                    try {
                        this.forceUpdate();
                        await Promise.all(promises);
                        // eslint-disable-next-line no-return-assign
                        wrappersToFetch.forEach((wrapper) => (wrapper.isFetching = false));
                        this.forceUpdate();
                    } catch (e) {
                        withDataConfig.handleError(e, this.showError);
                    }
                }

                render() {
                    if (this.error) {
                        return withDataConfig.renderError(this.error);
                    }
                    if (withDataConfig.fallback && this.wrappers.some((wrapper) => wrapper.isFetching)) {
                        return withDataConfig.fallback();
                    }
                    return super.render();
                }
            };
        };
    };
};

export default configureWithData;
